import { useState, useEffect, useCallback, useRef } from 'react';
import { downloadDesktopApp } from '../utils/downloadDesktopApp';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

// Extend the Window interface to include our global prompt store
declare global {
  interface Window {
    __PWA_DEFERRED_PROMPT: BeforeInstallPromptEvent | null;
  }
}

type InstallStatus = 'idle' | 'waiting' | 'prompting' | 'installed' | 'dismissed';

interface UsePWAInstall {
  /** True when the app is already running in standalone (installed) mode */
  isInstalled: boolean;
  /** True when the browser has offered the native install prompt */
  hasNativePrompt: boolean;
  /** True when the PWA can be installed (not already installed) */
  canInstall: boolean;
  /** Current install status for UI feedback */
  installStatus: InstallStatus;
  /** Trigger the native install prompt */
  install: () => Promise<void>;
}

export function usePWAInstall(): UsePWAInstall {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installStatus, setInstallStatus] = useState<InstallStatus>('idle');
  const resolveWaitRef = useRef<((_prompt: BeforeInstallPromptEvent) => void) | null>(null);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    if (isStandalone) {
      setIsInstalled(true);
      setInstallStatus('installed');
      return;
    }

    // Pick up the prompt that was captured globally before React mounted
    if (window.__PWA_DEFERRED_PROMPT) {
      setDeferredPrompt(window.__PWA_DEFERRED_PROMPT);
    }

    // Also listen for any future beforeinstallprompt events
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const prompt = e as BeforeInstallPromptEvent;
      window.__PWA_DEFERRED_PROMPT = prompt;
      setDeferredPrompt(prompt);

      // If install() was called and is waiting for the prompt, resolve it
      if (resolveWaitRef.current) {
        resolveWaitRef.current(prompt);
        resolveWaitRef.current = null;
      }
    };

    const handleAppInstalled = () => {
      window.__PWA_DEFERRED_PROMPT = null;
      setDeferredPrompt(null);
      setIsInstalled(true);
      setInstallStatus('installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    // Try the React state first, then the global store
    let prompt = deferredPrompt || window.__PWA_DEFERRED_PROMPT;

    if (!prompt) {
      // Prompt not available yet — wait up to 5 seconds for the SW to register
      // and the browser to fire beforeinstallprompt
      setInstallStatus('waiting');
      prompt = await new Promise<BeforeInstallPromptEvent | null>((resolve) => {
        resolveWaitRef.current = resolve as (p: BeforeInstallPromptEvent) => void;
        setTimeout(() => {
          resolveWaitRef.current = null;
          resolve(null);
        }, 5000);
      });
    }

    if (!prompt) {
      // Still no prompt — download a desktop shortcut installer instead
      setInstallStatus('idle');
      downloadDesktopApp();
      return;
    }

    try {
      setInstallStatus('prompting');
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;

      if (outcome === 'accepted') {
        window.__PWA_DEFERRED_PROMPT = null;
        setDeferredPrompt(null);
        setIsInstalled(true);
        setInstallStatus('installed');
      } else {
        // Clear the stale prompt — it cannot be reused after prompt() is called.
        // The browser will fire a new beforeinstallprompt event when ready.
        window.__PWA_DEFERRED_PROMPT = null;
        setDeferredPrompt(null);
        setInstallStatus('dismissed');
        // Reset after a moment so the user can try again
        setTimeout(() => setInstallStatus('idle'), 3000);
      }
    } catch (err) {
      // prompt.prompt() can throw if the prompt is stale or already used
      // eslint-disable-next-line no-console
      console.warn('[PWA] Install prompt failed:', err);
      window.__PWA_DEFERRED_PROMPT = null;
      setDeferredPrompt(null);
      setInstallStatus('idle');
    }
  }, [deferredPrompt]);

  const hasNativePrompt = !!(deferredPrompt || window.__PWA_DEFERRED_PROMPT);

  return {
    isInstalled,
    hasNativePrompt,
    canInstall: !isInstalled,
    installStatus,
    install,
  };
}
